/**
 * SatelliteImageryLayer Component
 *
 * Recent true-colour satellite imagery from NASA GIBS (Global Imagery Browse
 * Services). Shows what the landscape actually looks like from space — large
 * smoke plumes and fresh burn scars are visible. Supporter-gated in the UI
 * (satelliteImagery feature), but the tiles themselves are free and served
 * directly by NASA (no AWS cost), same pattern as SmokeLayer.
 *
 * Data source: NASA Worldview / GIBS — VIIRS SNPP Corrected Reflectance
 * Resolution: ~250 m
 * Update frequency: daily, ~1 day processing delay, cloud-dependent
 * Coverage: global
 * Cost: free
 */

import { useEffect, useRef } from 'react';
import { useMap } from 'react-leaflet';
import L from 'leaflet';

interface SatelliteImageryLayerProps {
  enabled?: boolean;
  opacity?: number;
  /**
   * Imagery day as YYYY-MM-DD. Defaults to the most recent available (yesterday,
   * to allow for GIBS processing delay). Reserved for a future date picker so
   * supporters can scrub back through days of imagery.
   */
  date?: string;
}

// NASA GIBS WMS endpoint (EPSG:3857 / Web Mercator — matches Leaflet).
const GIBS_WMS_URL = 'https://gibs.earthdata.nasa.gov/wms/epsg3857/best/wms.cgi';

// VIIRS SNPP true colour — 250 m, the sharpest daily true-colour product GIBS
// serves. Terra/Aqua MODIS true colour (also 250 m) are alternates if needed.
const TRUE_COLOR_LAYER = 'VIIRS_SNPP_CorrectedReflectance_TrueColor';

/** Most recent likely-available imagery day (yesterday), YYYY-MM-DD. */
function getDefaultImageryDate(): string {
  const d = new Date();
  d.setDate(d.getDate() - 1);
  return d.toISOString().split('T')[0];
}

export default function SatelliteImageryLayer({
  enabled = true,
  opacity = 1,
  date,
}: SatelliteImageryLayerProps) {
  const map = useMap();
  const layerRef = useRef<L.TileLayer.WMS | null>(null);

  useEffect(() => {
    if (layerRef.current) {
      map.removeLayer(layerRef.current);
      layerRef.current = null;
    }

    if (!enabled) return;

    const imageryDate = date || getDefaultImageryDate();

    try {
      const layer = L.tileLayer.wms(GIBS_WMS_URL, {
        layers: TRUE_COLOR_LAYER,
        format: 'image/png',
        transparent: true, // let no-data / off-swath areas show the basemap
        opacity,
        // @ts-expect-error - L.tileLayer.wms accepts extra WMS params (time)
        time: imageryDate,
        crs: L.CRS.EPSG3857,
        // True colour is imagery, so keep it below labels/markers. Leaflet's
        // tilePane is where base/overlay tiles live; markers sit in higher panes.
        pane: 'tilePane',
        attribution:
          'Imagery: <a href="https://worldview.earthdata.nasa.gov" target="_blank" rel="noopener">NASA VIIRS</a>',
      });

      layer.addTo(map);
      layerRef.current = layer;
      console.log(`[SatelliteImageryLayer] Added VIIRS true colour for ${imageryDate}`);
    } catch (err) {
      console.error('[SatelliteImageryLayer] Error adding imagery layer:', err);
    }

    return () => {
      if (layerRef.current) {
        map.removeLayer(layerRef.current);
        layerRef.current = null;
      }
    };
  }, [enabled, map, opacity, date]);

  return null;
}
